/**
 * Product Types Hook
 * Custom hook for managing product types data and operations
 */

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../../../contexts/AuthContext';
import toast from 'react-hot-toast';

export const useProductTypes = (options = {}) => {
  const { autoFetch = true } = options;
  
  const { token } = useAuth();
  const [productTypes, setProductTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch product types
  const fetchProductTypes = useCallback(async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Ensure authorization header is set
      if (token && !axios.defaults.headers.common['Authorization']) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      
      // Using backend modular route structure
      const response = await axios.get('/api/inventory/product-types');
      setProductTypes(response.data || []);
    } catch (err) {
      console.error('Error fetching product types:', err);
      setError(err.message);
      setProductTypes([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Create new product type
  const createProductType = useCallback(async (typeData) => {
    try {
      console.log('🔍 useProductTypes.createProductType - Original typeData:', typeData);
      console.log('🔍 useProductTypes.createProductType - Variants in typeData:', typeData.variants);

      // Clean the data: convert empty strings to undefined for numeric fields
      const cleanedData = {
        ...typeData,
        defaultHourlyRate: typeData.defaultHourlyRate === '' ? undefined : typeData.defaultHourlyRate,
        defaultDailyRate: typeData.defaultDailyRate === '' ? undefined : typeData.defaultDailyRate,
      };

      console.log('🔍 useProductTypes.createProductType - Cleaned data being sent to API:', cleanedData);
      console.log('🔍 useProductTypes.createProductType - Variants in cleaned data:', cleanedData.variants);

      const response = await axios.post('/api/inventory/product-types', cleanedData);
      // Don't show toast here, let the component handle it based on variant results
      await fetchProductTypes(); // Refresh list
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to create product type';
      toast.error(errorMessage);
      throw err;
    }
  }, [fetchProductTypes]);

  // Update existing product type
  const updateProductType = useCallback(async (typeId, typeData) => {
    try {
      console.log('🔍 useProductTypes.updateProductType - Original typeData:', typeData);
      console.log('🔍 useProductTypes.updateProductType - Variants in typeData:', typeData.variants);

      // Clean the data: convert empty strings to undefined for numeric fields
      const cleanedData = {
        ...typeData,
        defaultHourlyRate: typeData.defaultHourlyRate === '' ? undefined : typeData.defaultHourlyRate,
        defaultDailyRate: typeData.defaultDailyRate === '' ? undefined : typeData.defaultDailyRate,
      };

      console.log('🔍 useProductTypes.updateProductType - Cleaned data being sent to API:', cleanedData);
      console.log('🔍 useProductTypes.updateProductType - Variants in cleaned data:', cleanedData.variants);

      const response = await axios.put(`/api/inventory/product-types/${typeId}`, cleanedData);
      // Don't show toast here, let the component handle it based on variant results
      await fetchProductTypes(); // Refresh list
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to update product type';
      toast.error(errorMessage);
      throw err;
    }
  }, [fetchProductTypes]);

  // Delete product type
  const deleteProductType = useCallback(async (typeId) => {
    try {
      await axios.delete(`/api/inventory/product-types/${typeId}`);
      toast.success('Product type deleted successfully');
      await fetchProductTypes(); // Refresh list
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to delete product type';
      toast.error(errorMessage);
      throw err;
    }
  }, [fetchProductTypes]);

  // Update variants for a product type
  const updateVariants = useCallback(async (typeId, variants) => {
    try {
      const response = await axios.put(`/api/inventory/product-types/${typeId}/variants`, { variants });
      toast.success('Variants updated successfully');
      await fetchProductTypes(); // Refresh list
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to update variants';
      toast.error(errorMessage);
      throw err;
    }
  }, [fetchProductTypes]);

  // Get product type by ID
  const getProductTypeById = useCallback((typeId) => {
    return productTypes.find(type => type.id === typeId);
  }, [productTypes]);

  // Get variants for a product type
  const getVariantsForType = useCallback(async (typeId) => {
    try {
      const response = await axios.get(`/api/inventory/product-types/${typeId}/variants`);
      return response.data || [];
    } catch (err) {
      console.error('Error fetching variants:', err);
      return [];
    }
  }, []);

  // Auto-fetch on mount if enabled
  useEffect(() => {
    if (autoFetch && token) {
      fetchProductTypes();
    }
  }, [autoFetch, token]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    productTypes,
    loading,
    error,
    fetchProductTypes,
    createProductType,
    updateProductType,
    deleteProductType,
    updateVariants,
    getProductTypeById,
    getVariantsForType,
    // Computed values
    totalTypes: productTypes.length,
    categorizedTypes: productTypes.reduce((acc, type) => {
      const category = type.category || 'Uncategorized';
      if (!acc[category]) acc[category] = [];
      acc[category].push(type);
      return acc;
    }, {})
  };
};